import React from 'react';
import Head from 'next/head';
import type { InferGetServerSidePropsType, GetServerSideProps } from 'next';
import { ListingForm } from 'src/components/organisms';
import { FormInputValues } from 'src/components/organisms/listing-form';
import {
  defaultShippingInfo,
  defaultParcelInfo,
  Currency,
  defaultAuctionInfo,
  MarketplaceInfo,
} from 'src/api/v1-api';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

const CreateListing = ({ marketplace }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const initialValues: FormInputValues = {
    id: null,
    title: '',
    category: null,
    description: '',
    listing_attribute_values: [],
    listing_images: [],
    marketplace_account: null,
    status: 'draft',
    variants: [],
    shipping_info: defaultShippingInfo,
    parcel_info: defaultParcelInfo,
    listing_fulfillment_options: [],
    is_featured: false,
    currency: Currency.USD,
    auction_info: defaultAuctionInfo,
    sub_status: null,
  };

  const router = useRouter();
  let categoryId = router.query.category;
  if (Array.isArray(categoryId)) {
    categoryId = categoryId[0];
  }

  return (
    <>
      {marketplace && (
        <NextSeo
          title={`${marketplace.name} | Create Listing`}
          description={marketplace.description ? marketplace.description.slice(0, 120) : ''}
          openGraph={{
            site_name: marketplace.name,
            siteName: marketplace.name,
            title: `${marketplace.name} | Create Listing`,
            description: marketplace.description ? marketplace.description.slice(0, 120) : '',
            images: [
              {
                url: marketplace.logo_image,
                alt: `${marketplace.name} Logo`,
              },
            ],
          }}
        />
      )}
      <Head>
        <title>Create Listing</title>
        <meta name="description" content="Create Listing" />
      </Head>
      <ListingForm listing={initialValues} writingMode="Add" categoryId={categoryId} />
    </>
  );
};

export const getServerSideProps = (async ({ req, res }) => {
  try {
    const hostname = (req.headers?.host || '').split(':')[0];
    const marketplaceResponse = await fetch(
      `${process.env.NEXT_PUBLIC_API_HOST}/marketplaces/${hostname}/hostname/`,
    );

    const marketplace = await marketplaceResponse.json();

    res.setHeader('Cache-Control', 's-maxage=10, stale-while-revalidate=59');

    if (!marketplace) {
      return {
        notFound: true,
      };
    }
    return { props: { marketplace } };
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }
}) satisfies GetServerSideProps<{ marketplace: MarketplaceInfo }>;

export default CreateListing;
